import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from '../../auth/utils/authStorage';
import { truncateText } from '../utils/jobHelpers';
import { saveJob, unsaveJob, applyJob, cancelApplication } from '../../../services/api';
import { getMediaUrl } from '../../../utils/mediaUrl';
import CardIcon from '../../../shared/components/icons/CardIcon';

const statusMap = {
  pending: {
    icon: 'clock',
    title: 'Pending Review',
    subtitle: 'Your application is being reviewed by the employer.',
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    progress: ['? Applied', '?? Under review', '? Hired', '? Completed'],
  },
  hired: {
    icon: 'check',
    title: "You're Hired!",
    subtitle: 'Contact the employer to confirm next steps.',
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    progress: ['? Applied', '? Reviewed', '?? Hired', '? Completed'],
  },
  shortlisted: {
    icon: 'star',
    title: 'Shortlisted',
    subtitle: 'You have been shortlisted for this job. The employer may contact you about the next steps.',
    badge: 'bg-violet-50 text-violet-800 border-violet-200',
    progress: ['✓ Applied', 'Shortlisted', 'Hired', 'Completed'],
  },
  rejected: {
    icon: 'x',
    title: 'Not Selected',
    subtitle: 'The employer chose another applicant.',
    badge: 'bg-rose-50 text-rose-800 border-rose-200',
    progress: ['? Applied', '? Reviewed', '? Closed'],
  },
  completed: {
    icon: 'check',
    title: 'Completed',
    subtitle: 'Work finished and recorded.',
    badge: 'bg-sky-50 text-sky-800 border-sky-200',
    progress: ['? Applied', '? Reviewed', '? Hired', '?? Completed'],
  },
};

const getStatusInfo = (status) => {
  if (!status) return null;
  return statusMap[status.toLowerCase()] || {
    icon: 'circle',
    title: status,
    subtitle: 'Application update',
    badge: 'bg-slate-50 text-slate-800 border-slate-200',
    progress: ['? Applied'],
  };
};

const formatAppliedAt = (timestamp) => {
  if (!timestamp) return 'Applied recently';
  const date = new Date(timestamp);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return 'Applied just now';
  if (diff < 3600) return `Applied ${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `Applied ${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `Applied ${Math.floor(diff / 86400)}d ago`;
  return `Applied ${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
};

function JobCard({ job, onSave, onApply, onCancel, showActions = true, disableLink = false, linkTo, applicationView = false, savedView = false, onUnsave }) {
  const [isSaved, setIsSaved] = useState(job?.isSaved || false);
  const [hasApplied, setHasApplied] = useState(job?.hasApplied || false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const location = useLocation();
  const defaultJobDetailsPath = location.pathname.startsWith('/worker') ? `/worker/job/${job.id}` : `/job/${job.id}`;
  const jobDetailsPath = linkTo || defaultJobDetailsPath;

  const statusInfo = getStatusInfo(job.applicationStatus || job.status);
  const isApplicationCard = Boolean(job.applicationStatus || job.status);
  const displayLocation = [job.area, job.city].filter(Boolean).join(', ');

  const handleCancelApplication = async () => {
    if (!job.applicationId) return;
    setCancelLoading(true);
    try {
      await cancelApplication(job.applicationId);
      setCancelOpen(false);
      onCancel?.(job.applicationId);
    } catch (err) {
      console.error(err);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleUnsave = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await unsaveJob(job.id);
      onUnsave?.(job.id);
    } catch (err) {
      console.error(err);
    }
  };

  if (savedView) {
    const employer = job.user || job.employer || {};
    const employerName = employer.fullName || `${employer.firstName || ''} ${employer.lastName || ''}`.trim() || 'Employer';
    const employerInitials = `${employer.firstName?.[0] || ''}${employer.lastName?.[0] || ''}`.toUpperCase() || 'E';
    const employerPhoto = getMediaUrl(employer.profilePhoto || employer.profile?.profilePhoto);
    const status = String(job.status || 'open').toLowerCase();
    const statusInfo = { pending: ['OPEN', 'bg-amber-50 text-amber-800 border-amber-200', 'clock'], hired: ['FILLED', 'bg-emerald-50 text-emerald-800 border-emerald-200', 'check'], completed: ['COMPLETED', 'bg-sky-50 text-sky-800 border-sky-200', 'check'], closed: ['CLOSED', 'bg-slate-100 text-slate-700 border-slate-200', 'x'], open: ['OPEN', 'bg-amber-50 text-amber-800 border-amber-200', 'clock'] }[status] || [status.toUpperCase(), 'bg-slate-100 text-slate-700 border-slate-200', 'circle'];
    const handleSavedCardClick = () => navigate(jobDetailsPath, { state: { job, savedView: true } });
    return (
      <article
        role="link"
        tabIndex={0}
        onClick={handleSavedCardClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSavedCardClick();
          }
        }}
        className="cursor-pointer rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full bg-orange-100 font-bold text-orange-700">
              {employerPhoto ? <img src={employerPhoto} alt="" className="h-full w-full object-cover" /> : employerInitials}
            </div>
            <div className="min-w-0">
              <p className="truncate font-bold text-navy">{employerName}</p>
              <p className="text-sm text-slate-500">Employer</p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-orange-600"><CardIcon name="bookmark" />Saved</span>
        </div>

        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-xl font-bold uppercase text-navy">{job.title || 'Untitled job'}</h3>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${statusInfo[1]}`}><CardIcon name={statusInfo[2]} className="h-3.5 w-3.5" />{statusInfo[0]}</span>
          </div>
          <p className="mt-2 text-sm font-semibold text-slate-700">{job.skill?.name || job.skill?.category?.name || job.category || 'General work'}</p>
          <p className="mt-4 line-clamp-2 text-sm text-slate-600">{job.description || 'No description provided.'}</p>
          <div className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
            <p className="inline-flex items-center gap-1.5"><CardIcon name="location" />{displayLocation || job.location || 'Location unavailable'}</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="money" /><strong>K{job.budget ?? 0}</strong>{job.isNegotiable && ' · Negotiable'}</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="people" />{job.requiredPeople || 1} {job.requiredPeople === 1 ? 'person' : 'people'}</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="clock" />{(job.workType || job.jobType || 'PART_TIME').replace('_', ' ')}</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="flame" />{String(job.urgency || 'normal').toUpperCase()}</p>
          </div>
          <p className="mt-5 border-t border-slate-100 pt-4 text-sm font-semibold text-slate-700">{status === 'completed' ? 'This job has been completed.' : 'Saved job'}</p>
        </div>

        <div className="mt-5 flex justify-end border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleUnsave(event);
            }}
            className="rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Unsave
          </button>
        </div>
      </article>
    );
  }

  if (applicationView) {
    const applicationStatus = (job.applicationStatus || 'pending').toLowerCase();
    const applicationInfo = getStatusInfo(applicationStatus);
    const applicationIcon = applicationStatus === 'hired' || applicationStatus === 'completed' ? 'check' : applicationStatus === 'shortlisted' ? 'star' : applicationStatus === 'rejected' ? 'x' : 'clock';
    const appliedDate = job.applicationCreatedAt ? new Date(job.applicationCreatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Recently';
    const applicationEmployer = job.user || {};
    const applicationEmployerName = `${applicationEmployer.firstName || ''} ${applicationEmployer.lastName || ''}`.trim() || 'Employer';
    const applicationInitials = `${applicationEmployer.firstName?.[0] || ''}${applicationEmployer.lastName?.[0] || ''}`.toUpperCase();
    const applicationEmployerPhoto = getMediaUrl(applicationEmployer.profilePhoto || applicationEmployer.profile?.profilePhoto);

    return (
      <article className="cursor-pointer rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md" onClick={() => navigate(jobDetailsPath, { state: { job, applicationView: true } })} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') navigate(jobDetailsPath, { state: { job, applicationView: true } }); }} role="link" tabIndex="0">
        <div className="flex items-start justify-between gap-4"><span className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold ${applicationInfo.badge}`}><CardIcon name={applicationIcon} />{applicationInfo.title}</span><span className="shrink-0 text-sm font-medium text-slate-500">Applied {appliedDate}</span></div>
        <div className="mt-6">
          <h3 className="text-xl font-bold text-navy">{job.title}</h3>
          <p className="mt-2 text-sm font-semibold text-slate-700">{job.skill?.name || 'General work'}{job.skill?.category?.name && ` · ${job.skill.category.name}`}</p>
          <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{displayLocation || 'Location unavailable'}</p>
          <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="money" /><strong>K{job.budget ?? 0}</strong>{job.isNegotiable && ' · Negotiable'}</p>
          <div className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
            <p className="inline-flex items-start gap-1.5"><CardIcon name="people" className="mt-0.5 h-4 w-4 shrink-0" />{job.requiredPeople || 1} {job.requiredPeople === 1 ? 'worker needed' : 'workers needed'} · {job.slotsRemaining ?? 0} position{job.slotsRemaining === 1 ? '' : 's'} available</p>
            <p className="inline-flex items-center gap-1.5"><CardIcon name="clock" />{(job.workType || 'PART_TIME').replace('_', ' ')} · <CardIcon name="flame" />{String(job.urgency || 'normal').toUpperCase()}</p>
          </div>
          <div className={`mt-6 rounded-2xl p-4 text-sm ${applicationStatus === 'completed' ? 'bg-sky-50 text-sky-800' : applicationStatus === 'hired' ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-50 text-slate-700'}`}><p className="font-semibold">{applicationInfo.subtitle}</p></div>
        </div>
        <div className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5"><div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-orange-100 text-sm font-bold text-orange-700">{applicationEmployerPhoto ? <img src={applicationEmployerPhoto} alt="" className="h-full w-full object-cover" /> : applicationInitials || 'E'}</div><div><p className="text-sm font-semibold text-slate-800">{applicationEmployerName}</p><p className="text-xs text-slate-500">Employer{applicationEmployer.isWhatsapp ? ' · WhatsApp available' : ''}</p></div></div>
        {applicationStatus === 'pending' || applicationStatus === 'shortlisted' ? <div className="mt-5 border-t border-slate-100 pt-5"><button type="button" onClick={(event) => { event.stopPropagation(); setCancelOpen(true); }} className="w-full rounded-2xl border-2 border-navy px-4 py-3 text-sm font-semibold text-navy hover:bg-slate-50">Cancel Application</button></div> : null}
        {cancelOpen && <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4"><section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true"><h2 className="text-xl font-bold text-navy">Cancel application?</h2><p className="mt-2 text-sm text-slate-600">Your application for <strong>{job.title}</strong> will be cancelled.</p><div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setCancelOpen(false)} disabled={cancelLoading} className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700">Keep application</button><button type="button" onClick={handleCancelApplication} disabled={cancelLoading} className="rounded-2xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white">{cancelLoading ? 'Cancelling...' : 'Confirm cancel'}</button></div></section></div>}
      </article>
    );
  }

  const handleSaveClick = async (e) => {
    e.preventDefault();
    if (!auth?.token) {
      navigate('/login');
      return;
    }

    const nextSaved = !isSaved;
    setIsSaved(nextSaved);

    if (onSave) {
      onSave(job.id, nextSaved);
      return;
    }

    try {
      await (nextSaved ? saveJob(job.id) : unsaveJob(job.id));
    } catch (err) {
      setIsSaved(!nextSaved);
      console.error(err);
    }
  };

  const handleApplyClick = async (e) => {
    e.preventDefault();
    if (!auth?.token) {
      navigate('/login');
      return;
    }

    if (hasApplied) return;

    const nextApplied = true;
    setHasApplied(nextApplied);

    if (onApply) {
      onApply(job.id);
      return;
    }

    try {
      await applyJob(job.id);
    } catch (err) {
      setHasApplied(false);
      console.error(err);
    }
  };

  const cardContent = (
    <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold ${statusInfo.badge}`}>
            <CardIcon name={statusInfo.icon} />
            {statusInfo.title}
          </span>
        </div>
        {!isApplicationCard && showActions && (
          <button
            onClick={handleSaveClick}
            className="text-xl transition-colors duration-200"
            aria-label={isSaved ? 'Remove from saved' : 'Save job'}
          >
            {isSaved ? '?' : '?'}
          </button>
        )}
      </div>

      <div className="mb-4">
        <h3 className="text-lg font-bold text-navy mb-2">{job.title}</h3>
        {isApplicationCard ? (
          <p className="text-sm text-slate-600">{statusInfo.subtitle}</p>
        ) : (
          <p className="text-sm text-slate-600 line-clamp-2">{truncateText(job.description, 100)}</p>
        )}
      </div>

      <div className="grid gap-3 text-sm text-slate-700 sm:grid-cols-2 mb-4">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Location</p>
          <p className="mt-2 font-semibold text-slate-900">{displayLocation || 'Unknown'}</p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Budget</p>
          <p className="mt-2 font-semibold text-orange-600">K{job.budget}</p>
        </div>

        {isApplicationCard && (
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Applied</p>
            <p className="mt-2 font-semibold text-slate-900">{formatAppliedAt(job.applicationCreatedAt)}</p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        {isApplicationCard ? (
          <Link
            to={jobDetailsPath}
            state={{ job }}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            View Job Details
          </Link>
        ) : (
          <button
            onClick={handleApplyClick}
            disabled={hasApplied}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors duration-200 text-sm ${
              hasApplied
                ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                : 'bg-navy text-white hover:bg-slate-800'
            }`}
          >
            {hasApplied ? '? Applied' : 'Apply'}
          </button>
        )}
      </div>
    </article>
  );

  if (disableLink || isApplicationCard) {
    return cardContent;
  }

  return (
    <Link to={jobDetailsPath} state={{ job }} className="block">
      {cardContent}
    </Link>
  );
}

export default JobCard;
